import { Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { Header } from "@/components/layout/Header";
import { RelatedReads } from "@/components/seo/RelatedReads";
import { getPostsBySlugs } from "@/content/blogPosts";
import { Footer } from "@/components/layout/Footer";
import { SEOContentBlock } from "@/components/seo/SEOContentBlock";
import { Button } from "@/components/ui/button";
import { Sparkles, Heart, Users, Smile } from "lucide-react";
import heroImage from "@/assets/family-tubing.jpg";
import kidsRiver from "@/assets/gallery/kids-playing-river.jpg";
import cabanaScene from "@/assets/cabanas/cabana-riverside.jpg";

const FamilyGetawaysNewBraunfels = () => {
  return (
    <div className="min-h-screen">
      <SEOHead
        title="Family Getaways Near New Braunfels | Son's Guadalupe"
        description="Family getaways near New Braunfels: cabana rentals, tubing the Guadalupe, glamping & camping. The easiest stress-free Hill Country weekend."
        canonical="/family-getaways-new-braunfels"
        ogImage="/og/family-getaways.jpg"
      />
      <Header />
      <main className="pt-24">
        {/* Hero */}
        <section className="relative h-[55vh] min-h-[420px]">
          <img fetchPriority="high" loading="eager" decoding="async" src={heroImage} alt="Family enjoying a getaway tubing on the Guadalupe River near New Braunfels" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/40 to-black/20" />
          <div className="absolute inset-0 flex items-center">
            <div className="container-narrow mx-auto px-4 md:px-8">
              <div className="max-w-3xl">
                <h1 className="font-heading text-4xl md:text-6xl font-bold text-white leading-tight mb-4">
                  Family Getaways Near New Braunfels
                </h1>
                <p className="text-white/90 text-lg md:text-xl max-w-2xl mb-6">
                  The easiest, calmest, most kid-friendly way to do the Guadalupe River. Cabanas, tubing, glamping & tent camping — all from one private riverside basecamp.
                </p>
                <Link to="/book-now">
                  <Button size="lg" variant="default" className="text-lg px-8 py-6">Plan Your Family Getaway</Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="section-padding bg-background">
          <div className="container-narrow mx-auto max-w-3xl px-4 md:px-8">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">Fun Things to Do With Kids Near New Braunfels</h2>
            <p className="text-muted-foreground text-lg leading-relaxed mb-4">
              Looking for fun things to do with kids near New Braunfels? Son's Guadalupe is built for it. Tubing the Guadalupe River from your own private cabana, splashing in the shallow swimming holes, kayaking through cypress canyons, roasting s'mores at sunset, and stargazing from a glamping tent — every day on the river is a memory in the making.
            </p>
            <p className="text-muted-foreground text-lg leading-relaxed">
              Family getaways near New Braunfels at Son's mean no crowds, no chaos, and no parking-lot shuttles. Just your family, the river, and a private basecamp on the Guadalupe.
            </p>
          </div>
        </section>

        <section className="section-padding bg-muted/30">
          <div className="container-narrow mx-auto px-4 md:px-8">
            <h2 className="font-heading text-3xl font-bold text-foreground text-center mb-12">Why Families Pick Son's Guadalupe</h2>
            <div className="grid md:grid-cols-4 gap-6 max-w-5xl mx-auto">
              {[
                { icon: Sparkles, t: "Private cabanas", d: "Your own home base on the river — no scrambling for shade." },
                { icon: Heart, t: "Family-first", d: "Calm stretches, shallow swim areas, kid-friendly tubing." },
                { icon: Users, t: "Big group ready", d: "Multi-cabana setups for extended family & reunions." },
                { icon: Smile, t: "All-in-one", d: "Cabanas, tubing, glamping & camping in one place." },
              ].map((f, i) => (
                <div key={i} className="bg-card rounded-2xl p-6 shadow-soft text-center">
                  <f.icon className="w-10 h-10 text-primary mx-auto mb-3" />
                  <h3 className="font-heading font-bold text-lg text-foreground mb-2">{f.t}</h3>
                  <p className="text-muted-foreground text-sm">{f.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="section-padding bg-background">
          <div className="container-narrow mx-auto px-4 md:px-8 grid md:grid-cols-2 gap-10 items-center">
            <img src={kidsRiver} alt="Kids playing in the Guadalupe River near New Braunfels" className="w-full rounded-2xl shadow-elevated" loading="lazy" />
            <div>
              <h2 className="font-heading text-3xl font-bold text-foreground mb-4">A Day in the Life of a Family Getaway</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                Mornings start with coffee on your private cabana deck. Mid-morning, the kids grab tubes and float a calm, family-friendly stretch of the Guadalupe. Lunch is BBQ at the picnic table. Afternoon brings kayaking or a nap in the shade. Evening: campfire, s'mores, stories, stars. Repeat.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                It's the family getaway near New Braunfels you've been picturing — with none of the headaches.
              </p>
            </div>
          </div>
        </section>

        <section className="section-padding bg-muted/30">
          <div className="container-narrow mx-auto px-4 md:px-8 grid md:grid-cols-2 gap-10 items-center">
            <div className="order-2 md:order-1">
              <h2 className="font-heading text-3xl font-bold text-foreground mb-4">Mix & Match: Cabanas, Tubing, Glamping, Camping</h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                Build the trip that fits your family. Day-trippers love cabana rentals near New Braunfels with tubing the Guadalupe River. Multi-night families add glamping near New Braunfels or tent camping on River Road. Big groups book multiple cabanas side-by-side.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/cabanas"><Button variant="outline">Cabanas</Button></Link>
                <Link to="/tubing"><Button variant="outline">Tubing</Button></Link>
                <Link to="/glamping"><Button variant="outline">Glamping</Button></Link>
              </div>
            </div>
            <img src={cabanaScene} alt="Family enjoying a riverside cabana on the Guadalupe River" className="order-1 md:order-2 w-full rounded-2xl shadow-elevated" loading="lazy" />
          </div>
        </section>

        <section className="section-padding bg-background">
          <div className="container-narrow mx-auto text-center px-4 md:px-8">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">Ready to Plan Your Family Getaway?</h2>
            <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
              Pick your dates, pick your property, and let Son's Guadalupe handle the rest. Family getaways near New Braunfels start here.
            </p>
            <Link to="/book-now"><Button size="lg" variant="default" className="text-lg px-10 py-6">Book Your Family Getaway</Button></Link>
          </div>
        </section>
      </main>
      <SEOContentBlock
        eyebrow="The Guadalupe River Awaits"
        heading="Cabanas, Tubing, Glamping & Camping in One Place"
        paragraphs={[
          "Family getaways near New Braunfels are best when everything is in one spot. Son's Guadalupe combines cabana rentals on the Guadalupe River, tubing the Guadalupe River, glamping near New Braunfels, and tent camping on River Road into one easy-to-book riverside basecamp.",
          "Whether it's a day trip with kids or a multi-night family reunion, the Guadalupe River and the Texas Hill Country are waiting.",
        ]}
        links={[
          { label: "Cabanas", to: "/cabanas" },
          { label: "Tubing", to: "/tubing" },
          { label: "Glamping", to: "/glamping" },
          { label: "Tent Camping", to: "/tent-camping-new-braunfels" },
        ]}
      />
      <RelatedReads posts={getPostsBySlugs(["tubing-with-kids-guadalupe-river", "fun-things-to-do-with-kids-near-new-braunfels", "cabana-rentals-price-whats-included"])} heading="From our blog" />
      <Footer />
    </div>
  );
};

export default FamilyGetawaysNewBraunfels;
